/* PlayShop — Outil de centrage de carte (Phase 1 : centrage précis).
   - Photo face + pile, 8 points ajustables (4 coins extérieurs = bord carte, 4 coins intérieurs = cadre/illustration).
   - Correction de perspective par homographie → mesure indépendante de l'angle de prise de vue.
   - Calcul centrage H/V (pire axe) + estimation de note PSA / BGS / CGC / SGC (centrage uniquement).
   Aucune dépendance externe. Tout est client-side (aucune image envoyée à un serveur).
*/
(function () {
  'use strict';
  const root = document.querySelector('[data-ps-centering]');
  if (!root) return;

  /* ---------- Maths : homographie 4→4 (DLT) ---------- */
  // Résout H (3x3) tel que H·src ≈ dst pour 4 correspondances. src,dst = [{x,y}×4].
  function solve(A, b) { // élimination de Gauss, A = n×n, b = n
    const n = b.length;
    for (let i = 0; i < n; i++) {
      let p = i;
      for (let r = i + 1; r < n; r++) if (Math.abs(A[r][i]) > Math.abs(A[p][i])) p = r;
      [A[i], A[p]] = [A[p], A[i]]; [b[i], b[p]] = [b[p], b[i]];
      const piv = A[i][i] || 1e-12;
      for (let r = 0; r < n; r++) {
        if (r === i) continue;
        const f = A[r][i] / piv;
        for (let c = i; c < n; c++) A[r][c] -= f * A[i][c];
        b[r] -= f * b[i];
      }
    }
    return b.map((v, i) => v / (A[i][i] || 1e-12));
  }
  function homography(src, dst) {
    const A = [], b = [];
    for (let i = 0; i < 4; i++) {
      const { x, y } = src[i], { x: X, y: Y } = dst[i];
      A.push([x, y, 1, 0, 0, 0, -X * x, -X * y]); b.push(X);
      A.push([0, 0, 0, x, y, 1, -Y * x, -Y * y]); b.push(Y);
    }
    const h = solve(A, b); // h0..h7 ; h8=1
    return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
  }
  function applyH(H, p) {
    const w = H[6] * p.x + H[7] * p.y + H[8];
    return { x: (H[0] * p.x + H[1] * p.y + H[2]) / w, y: (H[3] * p.x + H[4] * p.y + H[5]) / w };
  }

  /* ---------- Estimation de note (centrage seul) ---------- */
  // r = pourcentage du plus grand côté (50 = parfait, 60 = 60/40…). front/back en "worst axis".
  function psa(f, b) { if (f <= 55 && b <= 75) return 10; if (f <= 60 && b <= 90) return 9; if (f <= 65) return 8; if (f <= 70) return 7; if (f <= 75) return 6; if (f <= 80) return 5; return 4; }
  function bgs(f, b) { if (f <= 50 && b <= 60) return 10; if (f <= 55 && b <= 60) return 9.5; if (f <= 60 && b <= 65) return 9; if (f <= 65) return 8.5; if (f <= 70) return 8; return 7; }
  function cgc(f, b) { if (f <= 55 && b <= 70) return 10; if (f <= 60 && b <= 80) return 9.5; if (f <= 65) return 9; if (f <= 70) return 8; return 7; }
  function sgc(f, b) { if (f <= 55 && b <= 60) return 10; if (f <= 60 && b <= 70) return 9.5; if (f <= 65) return 9; if (f <= 70) return 8; return 7; }
  const fmtGrade = (g) => Number.isInteger(g) ? String(g) : g.toFixed(1);

  const $ = (s, c) => (c || root).querySelector(s);
  const $$ = (s, c) => Array.from((c || root).querySelectorAll(s));
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const CORNERS = ['oTL', 'oTR', 'oBR', 'oBL', 'iTL', 'iTR', 'iBR', 'iBL'];
  const DST = [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];
  // Rayons d'arrondi (fraction de la largeur de carte) — réglables par curseurs. Défaut : carte ~4.5%, cadre ~1.8%.
  let RADIUS_OUT = 0.045, RADIUS_IN = 0.018;

  /* ---------- Une vue (face ou pile) ---------- */
  function CardView(panel, side, onChange, onManualLoad) {
    const canvas = $('[data-ps-canvas]', panel);
    const ctx = canvas.getContext('2d');
    const loupe = $('[data-ps-loupe]', panel);
    const lctx = loupe ? loupe.getContext('2d') : null;
    const fileInput = $('[data-ps-file]', panel);
    const drop = $('[data-ps-drop]', panel);
    let img = null, iW = 0, iH = 0;
    let view = { scale: 1, ox: 0, oy: 0 }; // image→canvas
    let pts = null; // {oTL:{x,y}...} en coords IMAGE
    let active = null, dragging = false, adjusted = false; // adjusted : l'utilisateur a bougé au moins un repère

    function fitCanvas() {
      const rect = panel.querySelector('[data-ps-stage]').getBoundingClientRect();
      const cw = Math.max(240, Math.floor(rect.width));
      const ch = Math.round(cw * 1.0); // stage carré, l'image est "contain" dedans
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = cw * dpr; canvas.height = ch * dpr;
      canvas.style.width = cw + 'px'; canvas.style.height = ch + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (img) computeView(cw, ch);
      render();
    }
    function computeView(cw, ch) {
      const s = Math.min(cw / iW, ch / iH);
      view.scale = s; view.ox = (cw - iW * s) / 2; view.oy = (ch - iH * s) / 2;
    }
    const toCanvas = (p) => ({ x: p.x * view.scale + view.ox, y: p.y * view.scale + view.oy });
    const toImage = (x, y) => ({ x: (x - view.ox) / view.scale, y: (y - view.oy) / view.scale });

    function initPoints() {
      // extérieur ~4% de marge, intérieur ~14% : l'utilisateur ajuste ensuite.
      const mo = 0.04, mi = 0.14;
      pts = {
        oTL: { x: iW * mo, y: iH * mo }, oTR: { x: iW * (1 - mo), y: iH * mo },
        oBR: { x: iW * (1 - mo), y: iH * (1 - mo) }, oBL: { x: iW * mo, y: iH * (1 - mo) },
        iTL: { x: iW * mi, y: iH * mi }, iTR: { x: iW * (1 - mi), y: iH * mi },
        iBR: { x: iW * (1 - mi), y: iH * (1 - mi) }, iBL: { x: iW * mi, y: iH * (1 - mi) },
      };
    }

    function loadFile(file) {
      if (!file || !/^image\//.test(file.type)) return;
      const url = URL.createObjectURL(file);
      const im = new Image();
      im.onload = () => {
        img = im; iW = im.naturalWidth; iH = im.naturalHeight;
        initPoints(); panel.classList.add('is-loaded');
        fitCanvas(); emit();
        URL.revokeObjectURL(url);
        onManualLoad && onManualLoad(); // photo chargée à la main = nouvelle carte (casse le lien de retouche)
      };
      im.src = url;
    }

    // Export JPEG compressé (downscale) pour la sauvegarde. Renvoie null si pas d'image
    // ou si le canvas est "tainted" (photo rechargée cross-origin en retouche → on garde la photo existante côté serveur).
    function exportJPEG(maxW) {
      if (!img) return null;
      try {
        maxW = maxW || 1100;
        const s = Math.min(1, maxW / iW);
        const w = Math.max(1, Math.round(iW * s)), h = Math.max(1, Math.round(iH * s));
        const oc = document.createElement('canvas'); oc.width = w; oc.height = h;
        oc.getContext('2d').drawImage(img, 0, 0, w, h);
        return oc.toDataURL('image/jpeg', 0.82);
      } catch (_) { return null; }
    }
    // Restaure un centrage sauvegardé : charge la photo (URL signée) + repose les 8 points.
    function loadState(url, savedPts) {
      if (!url) return;
      const im = new Image();
      im.onload = () => {
        img = im; iW = im.naturalWidth; iH = im.naturalHeight;
        if (savedPts && savedPts.oTL) {
          pts = {}; for (const k of CORNERS) pts[k] = { x: +savedPts[k].x, y: +savedPts[k].y };
          adjusted = true;
        } else { initPoints(); adjusted = false; }
        panel.classList.add('is-loaded'); fitCanvas(); emit();
      };
      im.src = url;
    }
    // Remet la vue à zéro (bouton "Nouvelle carte").
    function clear() { img = null; iW = 0; iH = 0; pts = null; adjusted = false; panel.classList.remove('is-loaded'); render(); emit(); }

    // Voisins (le long des 2 bords adjacents) → oriente le repère d'angle, même carte inclinée.
    const NB = {
      oTL: ['oTR', 'oBL'], oTR: ['oBR', 'oTL'], oBR: ['oBL', 'oTR'], oBL: ['oTL', 'oBR'],
      iTL: ['iTR', 'iBL'], iTR: ['iBR', 'iTL'], iBR: ['iBL', 'iTR'], iBL: ['iTL', 'iBR'],
    };
    const unit = (ax, ay) => { const l = Math.hypot(ax, ay) || 1; return { x: ax / l, y: ay / l }; };
    // Repère "coin arrondi" : 2 bras le long des bords + arrondi (Bézier passant vers le coin virtuel O).
    function bracket(Ok, color) {
      const O = toCanvas(pts[Ok]), A = toCanvas(pts[NB[Ok][0]]), B = toCanvas(pts[NB[Ok][1]]);
      const d1 = unit(A.x - O.x, A.y - O.y), d2 = unit(B.x - O.x, B.y - O.y);
      const tl = toCanvas(pts.oTL), tr = toCanvas(pts.oTR);
      const cardDim = Math.hypot(tr.x - tl.x, tr.y - tl.y) || 300;
      const rf = Ok[0] === 'o' ? RADIUS_OUT : RADIUS_IN;
      const R = Math.max(1, Math.min(cardDim * rf, 70)), arm = Math.max(22, cardDim * 0.06), on = active === Ok;
      const E1 = { x: O.x + d1.x * (R + arm), y: O.y + d1.y * (R + arm) };
      const E2 = { x: O.x + d2.x * (R + arm), y: O.y + d2.y * (R + arm) };
      ctx.beginPath(); ctx.moveTo(E1.x, E1.y);
      ctx.arcTo(O.x, O.y, E2.x, E2.y, R); // vrai arc de cercle (coin de carte)
      ctx.lineTo(E2.x, E2.y);
      ctx.strokeStyle = color; ctx.lineWidth = on ? 4 : 2.6; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.setLineDash([]); ctx.stroke();
      ctx.beginPath(); ctx.arc(O.x, O.y, on ? 5.5 : 3.5, 0, 7); ctx.fillStyle = on ? '#fff' : color; ctx.fill();
    }
    // Barre au milieu d'un bord (entre 2 coins) → visualise la ligne d'alignement.
    function edgeBar(Pk, Qk, color) {
      const P = toCanvas(pts[Pk]), Q = toCanvas(pts[Qk]);
      const a = { x: P.x + (Q.x - P.x) * 0.24, y: P.y + (Q.y - P.y) * 0.24 };
      const b = { x: P.x + (Q.x - P.x) * 0.76, y: P.y + (Q.y - P.y) * 0.76 };
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.setLineDash([]); ctx.stroke();
    }
    function render() {
      const cw = canvas.width / (window.devicePixelRatio || 1), ch = canvas.height / (window.devicePixelRatio || 1);
      ctx.clearRect(0, 0, cw, ch);
      if (!img) return;
      ctx.drawImage(img, view.ox, view.oy, iW * view.scale, iH * view.scale);
      // Bords (barres milieu) d'abord, puis les repères d'angle par-dessus.
      edgeBar('oTL', 'oTR', '#2FD16B'); edgeBar('oTR', 'oBR', '#2FD16B'); edgeBar('oBR', 'oBL', '#2FD16B'); edgeBar('oBL', 'oTL', '#2FD16B');
      edgeBar('iTL', 'iTR', '#5B7BFF'); edgeBar('iTR', 'iBR', '#5B7BFF'); edgeBar('iBR', 'iBL', '#5B7BFF'); edgeBar('iBL', 'iTL', '#5B7BFF');
      ['oTL', 'oTR', 'oBR', 'oBL'].forEach((k) => bracket(k, '#2FD16B')); // extérieur = bord carte
      ['iTL', 'iTR', 'iBR', 'iBL'].forEach((k) => bracket(k, '#5B7BFF')); // intérieur = cadre
    }
    function drawLoupe(cx, cy) {
      if (!lctx || !img) { if (loupe) loupe.classList.remove('is-on'); return; }
      const L = loupe.width;
      // Zoom INDÉPENDANT de la résolution photo : on montre toujours ~24% de la largeur de carte.
      // (sinon photo 4000px = zoom énorme illisible, photo 600px = quasi aucun zoom.)
      const cardWimg = Math.hypot(pts.oTR.x - pts.oTL.x, pts.oTR.y - pts.oTL.y) || iW;
      const srcR = Math.max(24, Math.min(cardWimg * 0.12, iW * 0.4));
      const ip = toImage(cx, cy);
      const lscale = L / (2 * srcR); // px image → px loupe
      lctx.clearRect(0, 0, L, L); lctx.save();
      lctx.imageSmoothingEnabled = true; lctx.imageSmoothingQuality = 'high';
      lctx.beginPath(); lctx.arc(L / 2, L / 2, L / 2, 0, 7); lctx.clip();
      lctx.drawImage(img, ip.x - srcR, ip.y - srcR, srcR * 2, srcR * 2, 0, 0, L, L);
      // Même repère "coin arrondi" que sur le canvas (cohérent) — pas de croix à angle droit.
      if (active && NB[active]) {
        const O = pts[active], A = pts[NB[active][0]], B = pts[NB[active][1]];
        const d1 = unit(A.x - O.x, A.y - O.y), d2 = unit(B.x - O.x, B.y - O.y);
        const cardDim = Math.hypot(pts.oTR.x - pts.oTL.x, pts.oTR.y - pts.oTL.y) || iW;
        const rf = active[0] === 'o' ? RADIUS_OUT : RADIUS_IN;
        const Ri = Math.max(1, Math.min(cardDim * rf, 120)) * lscale, arm = Math.max(cardDim * 0.06 * lscale, 26);
        const C = { x: L / 2 + (O.x - ip.x) * lscale, y: L / 2 + (O.y - ip.y) * lscale };
        const E1 = { x: C.x + d1.x * (Ri + arm), y: C.y + d1.y * (Ri + arm) };
        const E2 = { x: C.x + d2.x * (Ri + arm), y: C.y + d2.y * (Ri + arm) };
        lctx.beginPath(); lctx.moveTo(E1.x, E1.y); lctx.arcTo(C.x, C.y, E2.x, E2.y, Ri); lctx.lineTo(E2.x, E2.y);
        lctx.strokeStyle = active[0] === 'o' ? '#2FD16B' : '#5B7BFF'; lctx.lineWidth = 3; lctx.lineCap = 'round'; lctx.lineJoin = 'round'; lctx.stroke();
      }
      lctx.restore();
      // Position : la loupe SUIT le doigt mais décalée AU-DESSUS (jamais sous le doigt).
      // Trop près du haut → bascule en dessous. Clampée dans le stage.
      const cw = canvas.clientWidth, GAP = 36;
      let lx = cx - L / 2, ly = cy - L - GAP;
      if (ly < 4) { ly = 4; lx = (cx > cw / 2) ? 4 : (cw - L - 4); } // point trop haut → coin haut opposé au doigt
      lx = Math.max(4, Math.min(lx, cw - L - 4));
      loupe.style.left = lx + 'px'; loupe.style.top = ly + 'px'; loupe.style.right = 'auto';
      loupe.classList.add('is-on');
    }

    function hit(x, y) {
      let best = null, bd = 16 * 16;
      for (const k of CORNERS) { const c = toCanvas(pts[k]); const d = (c.x - x) ** 2 + (c.y - y) ** 2; if (d < bd) { bd = d; best = k; } }
      return best;
    }
    function localXY(e) { const r = canvas.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; }
    function onDown(e) {
      if (!img) return; const { x, y } = localXY(e); const k = hit(x, y);
      if (k) { active = k; dragging = true; canvas.setPointerCapture && e.pointerId != null && canvas.setPointerCapture(e.pointerId); drawLoupe(toCanvas(pts[k]).x, toCanvas(pts[k]).y); render(); e.preventDefault(); }
    }
    function onMove(e) {
      if (!dragging || !active) return; const { x, y } = localXY(e);
      const ip = toImage(clamp(x, view.ox, view.ox + iW * view.scale), clamp(y, view.oy, view.oy + iH * view.scale));
      pts[active] = { x: clamp(ip.x, 0, iW), y: clamp(ip.y, 0, iH) };
      adjusted = true;
      render(); drawLoupe(x, y); emit(); e.preventDefault();
    }
    function onUp() { dragging = false; if (loupe) loupe.classList.remove('is-on'); }
    function onKey(e) {
      if (!active || !img) return;
      const step = e.shiftKey ? 0.5 : 2; let dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = -step; else if (e.key === 'ArrowRight') dx = step;
      else if (e.key === 'ArrowUp') dy = -step; else if (e.key === 'ArrowDown') dy = step; else return;
      pts[active] = { x: clamp(pts[active].x + dx / view.scale, 0, iW), y: clamp(pts[active].y + dy / view.scale, 0, iH) };
      adjusted = true;
      render(); emit(); e.preventDefault();
    }

    // centrage : renvoie {h:[hi,lo], v:[hi,lo], worst} en % ou null
    function compute() {
      if (!img || !pts || !adjusted) return null; // rien tant que l'utilisateur n'a pas calé les repères
      const src = [pts.oTL, pts.oTR, pts.oBR, pts.oBL];
      const H = homography(src, DST);
      const q = {}; for (const k of ['iTL', 'iTR', 'iBR', 'iBL']) q[k] = applyH(H, pts[k]);
      const left = (q.iTL.x + q.iBL.x) / 2, right = 1 - (q.iTR.x + q.iBR.x) / 2;
      const top = (q.iTL.y + q.iTR.y) / 2, bottom = 1 - (q.iBL.y + q.iBR.y) / 2;
      if (left + right <= 0 || top + bottom <= 0) return null;
      const lp = left / (left + right) * 100, tp = top / (top + bottom) * 100;
      const h = [Math.max(lp, 100 - lp), Math.min(lp, 100 - lp)];
      const v = [Math.max(tp, 100 - tp), Math.min(tp, 100 - tp)];
      return { h, v, worst: Math.max(h[0], v[0]) };
    }

    function emit() { onChange && onChange(); }

    // events
    canvas.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchmove', onMove, { passive: false });
    canvas.addEventListener('touchend', onUp);
    canvas.setAttribute('tabindex', '0');
    canvas.addEventListener('keydown', onKey);
    fileInput && fileInput.addEventListener('change', (e) => loadFile(e.target.files[0]));
    if (drop) {
      ['dragover', 'dragenter'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.remove('is-drag'); }));
      drop.addEventListener('drop', (e) => loadFile(e.dataTransfer.files[0]));
      drop.addEventListener('click', () => fileInput && fileInput.click());
    }
    const resetBtn = $('[data-ps-reset]', panel);
    resetBtn && resetBtn.addEventListener('click', () => { if (img) { initPoints(); adjusted = false; render(); emit(); } });
    window.addEventListener('resize', fitCanvas);

    return { compute, side, hasImg: () => !!img, isAdjusted: () => adjusted, refit: fitCanvas, redraw: render,
      getPts: () => pts ? JSON.parse(JSON.stringify(pts)) : null, exportJPEG, loadState, clear };
  }

  /* ---------- Orchestration ---------- */
  const faceView = CardView($('[data-ps-panel="face"]'), 'face', update, clearEditing);
  const pileView = CardView($('[data-ps-panel="pile"]'), 'pile', update, clearEditing);
  // Curseurs d'arrondi (carte / cadre) → réglage manuel du rayon des repères (valeur = ‰ de la largeur carte).
  const rOut = $('[data-ps-radius-out]'), rIn = $('[data-ps-radius-in]');
  function applyRadius() {
    if (rOut) RADIUS_OUT = (+rOut.value) / 1000;
    if (rIn) RADIUS_IN = (+rIn.value) / 1000;
    faceView.redraw(); pileView.redraw();
  }
  if (rOut) rOut.addEventListener('input', applyRadius);
  if (rIn) rIn.addEventListener('input', applyRadius);
  applyRadius();
  const out = {
    fH: $('[data-ps-out="face-h"]'), fV: $('[data-ps-out="face-v"]'),
    pH: $('[data-ps-out="pile-h"]'), pV: $('[data-ps-out="pile-v"]'),
    grades: $('[data-ps-grades]'),
    score: $('[data-ps-score]'), worst: $('[data-ps-worst]'), quality: $('[data-ps-quality]'), axes: $('[data-ps-axes]'),
  };
  const ratio = (arr) => arr ? `${Math.round(arr[0])}/${Math.round(arr[1])}` : '-';
  const cls = (hi) => hi == null ? '' : (hi <= 55 ? 'is-good' : hi <= 62 ? 'is-mid' : 'is-bad');

  function setCell(el, arr) { if (!el) return; el.textContent = ratio(arr); el.className = 'ps-ct__ratio ' + (arr ? cls(arr[0]) : ''); }

  function update() {
    const f = faceView.compute(), p = pileView.compute();
    lastF = f; lastP = p;
    setCell(out.fH, f && f.h); setCell(out.fV, f && f.v);
    setCell(out.pH, p && p.h); setCell(out.pV, p && p.v);
    // estimation note : centrage face requis ; dos = 50 (neutre) si absent
    if (!f) {
      if (out.score) out.score.hidden = true;
      if (out.grades) out.grades.innerHTML = '<span class="ps-ct__hint">Charge la face et cale les repères pour obtenir l’estimation.</span>';
    } else {
      const bf = f.worst, bb = p ? p.worst : 50, w = Math.round(bf);
      if (out.score) out.score.hidden = false;
      if (out.worst) { out.worst.textContent = ratio([w, 100 - w]); out.worst.className = 'ps-ct__score-ratio ' + cls(w); }
      if (out.quality) {
        const q = w <= 55 ? 'Centrage excellent' : w <= 60 ? 'Bon centrage' : w <= 65 ? 'Centrage correct' : w <= 70 ? 'Décentrage visible' : 'Fort décentrage';
        out.quality.textContent = q; out.quality.className = 'ps-ct__score-quality ' + cls(w);
      }
      if (out.axes) out.axes.innerHTML = `Face avant ${ratio(f.h)} · ${ratio(f.v)}${p ? ` &nbsp;-&nbsp; Face arrière ${ratio(p.h)} · ${ratio(p.v)}` : ''}`;
      if (out.grades) {
        const rows = [['PSA', psa(bf, bb)], ['BGS', bgs(bf, bb)], ['CGC', cgc(bf, bb)], ['SGC', sgc(bf, bb)]];
        out.grades.innerHTML = rows.map(([n, g]) => `<div class="ps-ct__grade"><span class="ps-ct__grade-co">${n}</span><span class="ps-ct__grade-val">${fmtGrade(g)}</span></div>`).join('');
        // Track (throttlé) : une estimation aboutie = usage réel de l'outil de centrage
        try { var _n = Date.now(); if (!window.__psCtLast || _n - window.__psCtLast > 3000) { window.__psCtLast = _n; window.psTrack && window.psTrack('tool_centering_analyze', { worst: w, psa: psa(bf, bb), two_sided: !!p }); } } catch (_) {}
      }
    }
    syncSaveUI();
  }

  /* ---------- Sauvegarde / Mes cartes (compte, comme Mes Decks) ---------- */
  const FN = 'https://ipvzpgwgujamtzztxvcu.supabase.co/functions/v1/centering-store';
  const TOKEN = window.PS_AUTH_TOKEN || '';
  let lastF = null, lastP = null, editingId = null, listLoaded = false, listDirty = false;

  const tabs = $$('[data-ps-tab]');
  const panes = $$('[data-ps-pane]');
  const tabCount = $('[data-ps-tab-count]');
  const saveBar = $('[data-ps-save]');
  const nameInput = $('[data-ps-save-name]');
  const saveBtn = $('[data-ps-save-btn]');
  const msgEl = $('[data-ps-save-msg]');
  const editWrap = $('[data-ps-save-edit]');
  const newBtn = $('[data-ps-new]');
  const grid = $('[data-ps-saved-grid]');
  const emptyEl = $('[data-ps-saved-empty]');
  const toolbar = $('[data-ps-toolbar]');
  const toolbarLabel = $('[data-ps-toolbar-label]');

  const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch (_) { return ''; } };
  const setMsg = (t) => { if (msgEl) msgEl.textContent = t || ''; };

  function currentHeadline() {
    if (!lastF) return null;
    const bf = lastF.worst, bb = lastP ? lastP.worst : 50, w = Math.round(bf);
    return 'PSA ' + fmtGrade(psa(bf, bb)) + ' · ' + w + '/' + (100 - w);
  }
  function clearEditing() {
    editingId = null;
    if (editWrap) editWrap.hidden = true;
    if (saveBtn) saveBtn.textContent = '💾 Enregistrer cette carte';
    setMsg('');
  }
  function syncToolbar() {
    if (!toolbar) return;
    const loaded = faceView.hasImg() || pileView.hasImg();
    toolbar.hidden = !loaded;
    if (toolbarLabel) toolbarLabel.textContent = !loaded ? ''
      : (editingId ? ('✏️ Retouche : ' + ((nameInput && nameInput.value.trim()) || 'carte enregistrée')) : '📸 Nouveau centrage');
  }
  function syncSaveUI() {
    syncToolbar();
    if (!saveBar) return;
    const ready = faceView.hasImg() && faceView.isAdjusted();
    saveBar.hidden = !ready;
    if (saveBtn) saveBtn.textContent = editingId ? '💾 Mettre à jour' : '💾 Enregistrer cette carte';
    if (editWrap) editWrap.hidden = !editingId;
  }

  function doSave() {
    if (!TOKEN) { setMsg('Connecte-toi pour enregistrer.'); return; }
    if (!faceView.hasImg() || !faceView.isAdjusted()) { setMsg('Cale d’abord les repères de la face.'); return; }
    const bf = lastF.worst, bb = lastP ? lastP.worst : 50;
    const data = {
      face: { pts: faceView.getPts(), h: lastF.h, v: lastF.v, worst: lastF.worst },
      back: (pileView.hasImg() && pileView.isAdjusted() && lastP) ? { pts: pileView.getPts(), h: lastP.h, v: lastP.v, worst: lastP.worst } : null,
      grades: { psa: psa(bf, bb), bgs: bgs(bf, bb), cgc: cgc(bf, bb), sgc: sgc(bf, bb) },
    };
    const body = { name: (nameInput && nameInput.value.trim()) || 'Ma carte', headline: currentHeadline(), data };
    if (editingId) body.id = editingId;
    const fb = faceView.exportJPEG(); if (fb) body.face_b64 = fb;
    const bb64 = pileView.exportJPEG(); if (bb64) body.back_b64 = bb64;
    if (saveBtn) saveBtn.disabled = true; setMsg('Enregistrement…');
    fetch(FN + '?action=save', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-ps-token': TOKEN }, body: JSON.stringify(body) })
      .then((r) => r.json()).then((res) => {
        if (saveBtn) saveBtn.disabled = false;
        if (res && res.ok) { editingId = res.id; setMsg('✓ Enregistré dans « Mes cartes »'); syncSaveUI(); listDirty = true; }
        else setMsg('Erreur, réessaie.');
      }).catch(() => { if (saveBtn) saveBtn.disabled = false; setMsg('Erreur réseau.'); });
  }

  function renderCard(it) {
    const media = it.thumb_url
      ? `<div class="ps-card__media"><img class="ps-card__img" src="${it.thumb_url}" alt="${escapeHtml(it.name || 'Carte')}" loading="lazy"></div>`
      : `<div class="ps-card__media"><span class="ps-card__ph" aria-hidden="true">${escapeHtml((it.name || '?').slice(0, 1).toUpperCase())}</span></div>`;
    const grade = it.headline ? escapeHtml(it.headline) : 'Centrage';
    return `<article class="ps-card ps-ct__card">
      <button type="button" class="ps-card__link ps-ct__card-open" data-ps-open="${it.id}" title="Rouvrir et retoucher">
        ${media}
        <div class="ps-card__info">
          <p class="ps-card__type">Centrage</p>
          <h3 class="ps-card__title">${escapeHtml(it.name || 'Ma carte')}</h3>
          <span class="ps-card__cta ps-ct__card-grade">${grade}</span>
          <span class="ps-ct__card-date">${fmtDate(it.updated_at)}</span>
        </div>
      </button>
      <button type="button" class="ps-ct__card-del" data-ps-del="${it.id}" title="Supprimer" aria-label="Supprimer">🗑</button>
    </article>`;
  }
  function loadList() {
    if (!TOKEN || !grid) return;
    listLoaded = true; listDirty = false;
    grid.innerHTML = '<p class="ps-ct__saved-load">Chargement…</p>';
    fetch(FN + '?action=list', { method: 'POST', headers: { 'x-ps-token': TOKEN } })
      .then((r) => r.json()).then((res) => {
        const items = (res && res.items) || [];
        if (tabCount) { tabCount.textContent = items.length; tabCount.hidden = items.length === 0; }
        if (!items.length) { grid.innerHTML = ''; if (emptyEl) emptyEl.hidden = false; return; }
        if (emptyEl) emptyEl.hidden = true;
        grid.innerHTML = items.map(renderCard).join('');
      }).catch(() => { grid.innerHTML = '<p class="ps-ct__saved-load">Impossible de charger tes cartes.</p>'; });
  }
  function openSaved(id) {
    fetch(FN + '?action=load', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-ps-token': TOKEN }, body: JSON.stringify({ id }) })
      .then((r) => r.json()).then((res) => {
        if (!res || !res.ok) return;
        const it = res.item, d = it.data || {};
        faceView.loadState(it.photo_face_url, d.face && d.face.pts);
        if (it.photo_back_url) pileView.loadState(it.photo_back_url, d.back && d.back.pts);
        editingId = id;
        if (nameInput) nameInput.value = it.name || '';
        switchTab('centrage');
        setMsg('Retouche : ajuste les repères puis « Mettre à jour ».');
      });
  }
  function deleteSaved(id) {
    if (!window.confirm('Supprimer ce centrage ?')) return;
    fetch(FN + '?action=delete', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-ps-token': TOKEN }, body: JSON.stringify({ id }) })
      .then(() => { if (editingId === id) clearEditing(); loadList(); });
  }
  function switchTab(name) {
    tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.psTab === name));
    panes.forEach((p) => { p.hidden = p.dataset.psPane !== name; });
    if (name === 'cartes' && (listDirty || !listLoaded)) loadList();
  }

  tabs.forEach((t) => t.addEventListener('click', () => switchTab(t.dataset.psTab)));
  saveBtn && saveBtn.addEventListener('click', doSave);
  newBtn && newBtn.addEventListener('click', () => { faceView.clear(); pileView.clear(); if (nameInput) nameInput.value = ''; clearEditing(); });
  grid && grid.addEventListener('click', (e) => {
    const del = e.target.closest('[data-ps-del]'); if (del) { deleteSaved(del.getAttribute('data-ps-del')); return; }
    const open = e.target.closest('[data-ps-open]'); if (open) openSaved(open.getAttribute('data-ps-open'));
  });

  update();
  if (TOKEN) loadList(); // pré-charge le compteur + la grille "Mes cartes"
})();
